import { useCallback, type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import {
  buildCostQuotationRows,
  formatCostQuotationNumber,
  getCostQuotationStyleNo,
  getCostQuotationTitlePartcode,
  resolveCostQuotationImageUrl,
  text,
  type CostQuotationCandidate,
  type CostQuotationCard,
  type CostQuotationCardData,
  type CostQuotationPartcode,
} from '@/utils/approval/costQuotation';
import { CostQuotationRoot } from './styles';

/** 카드 제목 칸 — 조회·작성 화면이 이 칸에 부분반려·삭제 버튼을 붙임 */
const TITLE_ATTR = 'data-cq-card-title';

const StatusTable = ({
  card,
  t,
}: {
  card: CostQuotationCardData;
  t: TFunction;
}) => {
  const h = card.header ?? {};
  const labels = [
    t('브랜드'),
    t('시즌'),
    t('스타일'),
    t('화폐'),
    t('환율(KRW)'),
    t('수량'),
    t('생산국가'),
    t('협력사'),
    t('협력사(담당자)'),
    t('소싱대행'),
    t('CNY 환율'),
    t('CNY 확정가'),
  ];
  return (
    <table className="text-center">
      <tbody>
        <tr>
          <td colSpan={labels.length} className="label">
            {t('현황')}
          </td>
        </tr>
        <tr>
          {labels.map((label) => (
            <td key={label} className="label">
              {label}
            </td>
          ))}
        </tr>
        <tr>
          <td>{text(h.brand)}</td>
          <td>{text(h.season)}</td>
          <td>{text(h.partcode)}</td>
          <td>{text(h.currCd)}</td>
          <td>{formatCostQuotationNumber(h.currExch)}</td>
          <td>{formatCostQuotationNumber(h.qty)}</td>
          <td>{text(h.originName)}</td>
          <td>{text(h.custName)}</td>
          <td>
            {text(h.person)}
            <br />
            {text(h.personTel)}
          </td>
          <td>{text(h.cnYn)}</td>
          <td>{formatCostQuotationNumber(h.cnCnyExch, 2)}</td>
          <td>{formatCostQuotationNumber(h.cnQuotAmt, 2)}</td>
        </tr>
      </tbody>
    </table>
  );
};

const CostTable = ({
  card,
  styleNo,
  t,
}: {
  card: CostQuotationCardData;
  styleNo: string;
  t: TFunction;
}) => {
  const curr = text(card.header?.currCd) || text(card.currency?.curr);
  const imageUrl = resolveCostQuotationImageUrl(card.header);
  const rows = buildCostQuotationRows(card);
  return (
    <table className="cq-cost">
      <colgroup>
        <col width="50%" />
        <col width="15%" />
        <col width="12%" />
        <col width="13%" />
        <col width="10%" />
      </colgroup>
      <tbody>
        <tr>
          <td rowSpan={rows.length + 2} className="cq-image">
            {imageUrl && <img src={imageUrl} alt={styleNo} />}
          </td>
          <td rowSpan={2} className="label">
            <strong>{t('항목')}</strong>
          </td>
          <td className="label">
            <strong>{t('견적서(협력사)')}</strong>
          </td>
          <td colSpan={2} className="label">
            <strong>{t('확정가(본사)')}</strong>
          </td>
        </tr>
        <tr>
          <td className="label">
            <strong>{curr}</strong>
          </td>
          <td className="label">
            <strong>{curr}</strong>
          </td>
          <td className="label">
            <strong>KRW</strong>
          </td>
        </tr>
        {rows.map((row, i) => (
          <tr
            key={`${row.typeCode}-${i}`}
            className={row.highlighted ? 'cq-highlight' : undefined}
          >
            <td className="text-center">{row.label}</td>
            <td className="text-right">{row.currQuotAmt}</td>
            <td className="text-right">{row.quotAmt}</td>
            <td className="text-right">
              {row.emphasized ? (
                <span className="cq-emphasis">{row.negoAmt}</span>
              ) : (
                row.negoAmt
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const CardView = ({
  card,
  partcode,
  aprvRev,
  styleNo,
  t,
}: {
  card: CostQuotationCardData;
  partcode: string;
  aprvRev: string;
  styleNo: string;
  t: TFunction;
}) => {
  const aprvId = text(card.aprvId);
  const titleAttrs = {
    [TITLE_ATTR]: '',
    'data-aprv-id': aprvId,
    'data-partcode': partcode,
    'data-aprv-rev': aprvRev,
  };
  return (
    // 백엔드가 부분 반려된 카드를 조회 때 이 data-item-id 로 찾아 뺌
    <div className="cq-card" data-cq-card={aprvId} data-item-id={aprvId}>
      <table>
        <tbody>
          <tr>
            <th {...titleAttrs}>
              {t('원가 견적서({{partcode}}, {{aprvRev}}차)', {
                partcode,
                aprvRev,
              })}
            </th>
          </tr>
        </tbody>
      </table>
      <StatusTable card={card} t={t} />
      <CostTable card={card} styleNo={styleNo} t={t} />
    </div>
  );
};

/** 카드 1장 — 작성 시점 HTML 로 굳혀 attributes.content 에 이어 붙임 */
export const buildCostQuotationCard = (
  card: CostQuotationCardData,
  partcodes: CostQuotationPartcode[] | null | undefined,
  candidate: CostQuotationCandidate | undefined,
  t: TFunction,
): CostQuotationCard => {
  const styleNo = getCostQuotationStyleNo(card, partcodes, candidate);
  const partcode = getCostQuotationTitlePartcode(card, candidate);
  // 차수는 본문 응답에 없어 미상신 목록 값
  const aprvRev = text(candidate?.aprvRev);
  return {
    aprvId: text(card.aprvId),
    styleNo,
    html: renderToStaticMarkup(
      <CardView
        card={card}
        partcode={partcode}
        aprvRev={aprvRev}
        styleNo={styleNo}
        t={t}
      />,
    ),
  };
};

export type CostQuotationCardRef = {
  aprvId: string;
  partcode: string;
  aprvRev: string;
};

/** 레거시 저장 HTML 의 상세내역 3종 버튼 — 불러올 API 가 없어 숨김 */
const LEGACY_DETAIL_BUTTONS = [
  'btn-detail-open',
  'btn-supply-open',
  'btn-etc-cost-open',
];

/**
 * 저장 본문 그대로(레거시 문서 포함). 카드 제목 칸에 화면별 버튼을 붙임.
 * 레거시 문서는 저장 HTML 안의 삭제·부분반려 버튼 자리를 씀
 */
export const CostQuotationSnapshot = ({
  content,
  renderCardAction,
}: {
  content: string;
  renderCardAction?: (card: CostQuotationCardRef) => ReactNode;
}) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (tagName === 'th' && element.hasAttribute(TITLE_ATTR)) {
        const action = renderCardAction?.({
          aprvId: element.getAttribute('data-aprv-id') ?? '',
          partcode: element.getAttribute('data-partcode') ?? '',
          aprvRev: element.getAttribute('data-aprv-rev') ?? '',
        });
        return (
          <th key={props.key as string} className="cq-card-title">
            <span className="cq-card-title-row">
              <span>{children}</span>
              {action}
            </span>
          </th>
        );
      }
      if (tagName !== 'button') return undefined;
      if (element.classList.contains('btn-detail-hide')) return null;
      if (LEGACY_DETAIL_BUTTONS.some((c) => element.classList.contains(c))) {
        return null;
      }
      if (element.classList.contains('btn-detail-reject')) {
        return (
          renderCardAction?.({
            aprvId: element.getAttribute('data-aprv_id') ?? '',
            partcode: element.getAttribute('data-partcode') ?? '',
            aprvRev: element.getAttribute('data-aprv_rev') ?? '',
          }) ?? null
        );
      }
      return undefined;
    },
    [renderCardAction],
  );
  return (
    <CostQuotationRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </CostQuotationRoot>
  );
};

/** 부분반려 버튼을 붙일 카드 수 — V2 카드 제목 칸 + 레거시 부분반려 버튼 */
export const countCostQuotationCards = (content: string | null) =>
  content
    ? (content.match(/data-cq-card-title|btn-detail-reject/g) ?? []).length
    : 0;
